import { Component, OnInit, Input, Output, EventEmitter, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { faAngleLeft, faPlus, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { NgbActiveModal, NgbModal, NgbModalRef, NgbTypeaheadSelectItemEvent, NgbTypeahead, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Observable, debounceTime, distinctUntilChanged, merge, Subject, switchMap, map } from 'rxjs';
import { AlertService } from '../../../alert.service';
import { Client } from '../../../client';
import { ClientService } from '../../../client.service';
import { NewSiteModalComponent } from '../../../new-site-modal/new-site-modal.component';
import { Site } from '../../../site';
import { SiteService } from '../../../site.service';
import { User } from '../../../user';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgIf, NgFor } from '@angular/common';
import { ProjectProposals } from '@main/proposal';
import { ProposalService } from '@main/proposal.service';
import { Project } from '@main/project';
import { MultiYearAgreement, MyaProjectCandidate } from '@app-shared/interfaces/multi-year-agreement/multi-year-agreement';
import { MultiYearAgreementService } from '@app-shared/services/multi-year-agreement.service';
import { MultiYearAgreementStatusPipe } from '@app-shared/pipes/multi-year-agreements.pipe';
import { Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';


@Component({
  selector: 'app-assign-project-to-mya-modal',
  providers: [MultiYearAgreementStatusPipe],
  imports: [CommonModule, ReactiveFormsModule, NgIf, NgbTypeahead, NgFor, NgbTooltip, FaIconComponent, MultiYearAgreementStatusPipe],
  templateUrl: './assign-project-to-mya-modal.component.html',
  styleUrl: './assign-project-to-mya-modal.component.css'
})
export class AssignProjectToMyaModalComponent implements OnInit {

  // Modals
  private modalReference?: NgbModalRef;

  // Properties
  assignedProjectToMyaForm: UntypedFormGroup;
  @Input() clientId = signal<string>('');
  @Input() siteId = signal<string | null>(null);
  @Input() projectId = signal<string | null>(null);
  @Input() myaId = signal<string | null>(null);
  @Input() myaProjectYear = signal<string | null>(null);
  @Input() type = signal<string | null>(null);
  @Input() acceptedProposal = signal<string | null>(null);
  @Input() selectedProject?: Project | MyaProjectCandidate;

  sites: Site[] = [];
  showAddSiteButton: boolean = false;
  loginDetails: User;
  optionalStagesAreCollapsed: boolean = true;
  projectProposals = signal<ProjectProposals[]>([]);
  multiYearAgreements = signal<MultiYearAgreement[]>([]);

  selectedMya = signal<MultiYearAgreement | null>(null);
  selectedProjectYear = signal<number | null>(null);
  @Output() okay = new EventEmitter<any>();

  // Clients
  clientTypeaheadInput: (text$: Observable<string>) => Observable<Client[]>;
  clientTypeaheadInputFocus$ = new Subject<string>();
  clientResultFormatter = (client: Client) => client.name;
  selectedClient!: Client;

  // Project Candidates
  projectNameTypeaheadInput: (text$: Observable<string>) => Observable<MyaProjectCandidate[]>;
  projectNameTypeaheadInputFocus$ = new Subject<string>();
  projectInputFormatter = (candidate: MyaProjectCandidate | string) => typeof candidate === 'string' ? candidate : candidate?.name ?? '';
  projectResultFormatter = (candidate: MyaProjectCandidate | string) => typeof candidate === 'string' ? candidate : candidate?.name ?? '';

  // Font Awesome Properties
  faAngleLeft = faAngleLeft;
  faPlus = faPlus;
  faCaretRight = faCaretRight;
  faCaretDown = faCaretRight

  constructor(private clientService: ClientService,
    private siteService: SiteService,
    private alertService: AlertService,
    private proposalService: ProposalService,
    private modalService: NgbModal,
    private multiYearAgreementService: MultiYearAgreementService,
    public modal: NgbActiveModal) { }

  ngOnInit(): void {
    this.loginDetails = JSON.parse(JSON.stringify(localStorage.getItem('login')));
    this.assignedProjectToMyaForm = new UntypedFormGroup({
      client: new UntypedFormControl(),
      site: new UntypedFormControl(),
      projectName: new UntypedFormControl(this.selectedProject ? this.selectedProject.name : null, [Validators.required]),
      proposal: new UntypedFormControl(null, [Validators.required]),
      mya: new UntypedFormControl(),
    });
    this.assignedProjectToMyaForm.controls.site.disable();
    this.assignedProjectToMyaForm.controls.proposal.disable();
    this.assignedProjectToMyaForm.controls.projectName.valueChanges.subscribe((value) => {
      this.onProjectNameChange(value);
    });
    this.prepareClientTypeahead();
    this.prepareProjectNameTypeahead();
    if (this.clientId()) this.getClient();
    if (this.projectId() && this.siteId()) this.getMultiYearAgreements();
    if(this.type() === 'PROJECT_DETAIL') {
      this.getProjectProposals();
    }
  }

  // Open New Site Modal
  openNewSiteModal(): void {
    this.modalReference = this.modalService.open(NewSiteModalComponent);
    this.modalReference.componentInstance.client = this.selectedClient;
    this.modalReference.componentInstance.siteCreated.subscribe(() => {
      this.getSites();
    });
  }

  /* ----- Clients ----- */

  // Get Client
  private getClient(): void {
    this.clientService.getClient(this.clientId()).subscribe((client) => {
      this.selectedClient = client;
      this.assignedProjectToMyaForm.controls.client.setValue(this.selectedClient.name);
      this.assignedProjectToMyaForm.controls.client.disable();
      this.getSites();
    });
  }

  // Get Clients
  private getClients(term: string): Observable<Client[]> {
    const params = {
      searchTerm: term,
      sortBy: 'client_name',
      sortDirection: 'ASC',
      limit: 10,
      offset: 0
    };
    return this.clientService.getClients(params).pipe(map((res) => { return res.clients; }));
  }

  // Prepare Client Typeahead
  private prepareClientTypeahead(): void {
    this.clientTypeaheadInput = (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(debounceTime(250), distinctUntilChanged());
      return merge(debouncedText$, this.clientTypeaheadInputFocus$).pipe(switchMap((term) => {
        return this.getClients((term.length == 0) ? null : term);
      }));
    }
  }

  // Select Client
  selectClient(event: NgbTypeaheadSelectItemEvent): void {
    event.preventDefault();
    this.selectedClient = event.item;
    this.assignedProjectToMyaForm.controls.client.setValue(this.selectedClient.name);
    this.assignedProjectToMyaForm.controls.client.disable();
    this.getSites();
  }

  /* ----- Sites ----- */

  // Get Sites
  getSites(): void {
    const params = {
      searchTerm: null,
      sortBy: 'site_name',
      sortDirection: 'ASC',
      limit: null,
      offset: null,
      'filter:client_id': JSON.stringify([this.selectedClient.id])
    };
    this.siteService.getSites(params).subscribe((res) => {
      this.sites = res.sites;
      if (this.sites.length > 0) {
        this.assignedProjectToMyaForm.controls.site.enable();
        if (this.siteId()) this.assignedProjectToMyaForm.controls.site.setValue(this.siteId()!);
        else this.assignedProjectToMyaForm.controls.site.setValue(this.sites[0].id);
        this.assignedProjectToMyaForm.controls.site.disable();
        this.showAddSiteButton = false;
      } else {
        this.assignedProjectToMyaForm.controls.site.disable();
        this.alertService.showWarningAlert('No Sites Available');
        this.showAddSiteButton = true;
      }
    });
  }

  // Prepare Project Name Typeahead
  private prepareProjectNameTypeahead(): void {
    this.projectNameTypeaheadInput = (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(
        debounceTime(250),
        distinctUntilChanged()
      );

      return merge(
        debouncedText$,
        this.projectNameTypeaheadInputFocus$
      ).pipe(
        switchMap((term) => {
          return this.getProjectCandidates(term);
        })
      );
    };
}

  // Get Project Candidates (projects assignable to this MYA, scoped by the API to the MYA's site/client and missing years)
  private getProjectCandidates(term: string): Observable<MyaProjectCandidate[]> {
    return this.multiYearAgreementService.getMyaProjectsByMyaId(this.myaId(), term).pipe(map((res: any) => {
      return res;
    }));
  }

  selectProject(event: NgbTypeaheadSelectItemEvent): void {
    event.preventDefault();
    const candidate = event.item as MyaProjectCandidate | null;
    const projectName = candidate?.name || '';
    if (!candidate || !projectName) {
      this.resetProjectProposalState();
      return;
    }
    this.selectedProject = candidate;
    this.projectProposals.set([]);
    this.proposal.reset();
    this.proposal.disable();
    this.assignedProjectToMyaForm.controls.projectName.setValue(projectName, { emitEvent: false });
    this.getProjectProposals();
  }

  private onProjectNameChange(value: string | null): void {
    if (!value || !this.selectedProject || this.selectedProject.name !== value) {
      this.resetProjectProposalState();
    }
  }

  private resetProjectProposalState(): void {
    this.selectedProject = undefined;
    this.projectProposals.set([]);
    this.proposal.reset();
    this.proposal.disable();
  }

  // Get Project Proposals
  getProjectProposals(): void {
    this.proposalService.getProposalsByProjectId(this.selectedProject?.id || this.projectId(), 'MULTI').subscribe((res: { proposals: ProjectProposals[] }) => {
      console.log("res.proposals", res);
      this.projectProposals.set(res?.proposals);
      if (this.projectProposals().length > 0) {
        this.proposal.enable();
        // check has accepted proposal
        const acceptedProposal = this.projectProposals().find(proposal => proposal.projectStatus === 'ACCEPTED');
        if (acceptedProposal) {
          this.assignedProjectToMyaForm.controls.proposal.setValue(acceptedProposal.proposalId);
        }
        // check if only one proposal exists
        else if (this.projectProposals().length === 1) {
          this.assignedProjectToMyaForm.controls.proposal.setValue(this.projectProposals()[0].proposalId);
        }
        // check if no accepted proposal and multiple Pending/In Progress proposals
        if (this.projectProposals().length > 1 && !acceptedProposal) {
          //set the first proposal
          this.assignedProjectToMyaForm.controls.proposal.setValue(this.projectProposals()[0].proposalId);
        }
      } else {
        this.proposal.reset();
        this.proposal.disable();
      }
    });
  }

  // Get MultiYear Agreements
  private getMultiYearAgreements(): void {
    this.multiYearAgreementService.getMultiYearAgreementsBySiteId(this.siteId()).subscribe((res: MultiYearAgreement[]) => {
      const filterMYAByProjectYear = res.filter(mya => {
        // Filter MYA Project Year greater than or equal to mya final year
        return mya.finalYear >= this.selectedProjectYear();
      });
      this.multiYearAgreements.set(filterMYAByProjectYear);
    });
  }

  // Select Multi-Year Agreement
  selectMultiYearAgreement(event: any): void {
    event.preventDefault();
    const selectedId = event.target.value;
    const selectedMYA = this.multiYearAgreements().find(mya => mya.id == selectedId);
    this.selectedMya.set(selectedMYA);
  }
  // Add Project
  addProjectToMya(): void {
    if (this.assignedProjectToMyaForm.valid) {
      const mya = {
        myaId: this.myaId() || this.selectedMya()?.id,
        projectId: this.projectId() || this.selectedProject?.id,
        proposalId: this.assignedProjectToMyaForm.value.proposal || this.acceptedProposal(),
        myaProjectYear: this.selectedMya()?.startYear || this.myaProjectYear(),
        clientId: this.clientId(),
        siteId: this.assignedProjectToMyaForm.getRawValue().site,

      };
      this.multiYearAgreementService.addProjectToMya(mya).subscribe((response) => {
        this.alertService.showSuccessAlert('Project successfully assigned to Multi-Year Agreement');
        this.modal.close();
        this.okay.emit(response);
      });
    } else {
      this.assignedProjectToMyaForm.markAllAsTouched();
    }
  }

  // Project Form Accessors
  get site() { return this.assignedProjectToMyaForm.controls.site; }
  get name() { return this.assignedProjectToMyaForm.controls.name; }
  get projectName() { return this.assignedProjectToMyaForm.controls.projectName; }
  get proposal() { return this.assignedProjectToMyaForm.controls.proposal; }
  get mya() { return this.assignedProjectToMyaForm.controls.mya; }

}
